import { collection, doc, getDocs, getDoc, query, where, writeBatch, serverTimestamp, increment, updateDoc, runTransaction
} from "firebase/firestore";
import { db } from "./firebase";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { toISODate } from "../utils/date";
import { COLLECTIONS } from "../services/collections";
import { WEEKDAY_LABELS } from "../constants/calendar";
import { TRIAL_ATTENDANCE_STATUS } from "../constants/enrollments";

/**
 * EnrollmentService
 * 
 * Regras de Negócio:
 * - Se ativo: Matrícula afeta todas as sessões da data em diante (type="regular").
 * - Se inativo/lead: Matrícula afeta apenas uma única sessão (type="trial").
 * - Desnormalização: Os dados do aluno são gravados no array `attendances` da sessão.
 * - Contadores separados: `enrolledCount` (regulares) e `experimentalCount` (experimentais) na sessão.
 * - Turma (class): `enrolledCount` é incrementado ao matricular aluno regular.
 */

export const EnrollmentService = {

    /**
     * Helper INTERNO para atualizar as atividades exibidas no documento do Membro.
     * Busca todas as matrículas ativas e salva a lista única de nomes de atividade
     * no campo `activityNames` do membro.
     */
    _updateMemberActivities: async (idTenant, idMember) => {
        try {
            const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
            const q = query(
                enrollmentsRef,
                where("idMember", "==", idMember),
                where("status", "==", "active"),
                where("type", "==", "regular") // Apenas matrículas regulares contam como atividade
            );
            const snapshot = await getDocs(q);

            const activityNamesSet = new Set();
            const classIdsSet = new Set();

            snapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.activityName) {
                    activityNamesSet.add(data.activityName);
                }
                if (data.idClass) {
                    classIdsSet.add(data.idClass);
                }
            });

            const activityNames = Array.from(activityNamesSet);
            const enrolledClassIds = Array.from(classIdsSet);

            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            await updateDoc(memberRef, {
                activityNames,
                enrolledClassIds,
                updatedAt: serverTimestamp()
            });

            return activityNames;
        } catch (error) {
            console.error("[EnrollmentService] Erro ao atualizar activityNames do membro:", error);
            return []; // Não falha a operação principal se der erro
        }
    },

    /**
     * Lista todas as matrículas ativas de um membro
     */
    listMemberEnrollments: async (idTenant, idMember) => {
        if (!idTenant || !idMember) return [];

        try {
            const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
            const q = query(
                enrollmentsRef,
                where("idMember", "==", idMember),
                where("status", "==", "active")
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("[EnrollmentService] Erro ao listar matrículas:", error);
            return [];
        }
    },

    /**
     * Lista TODAS as matrículas de um membro (ativas + canceladas)
     */
    listAllMemberEnrollments: async (idTenant, idMember) => {
        if (!idTenant || !idMember) return [];

        try {
            const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
            const q = query(
                enrollmentsRef,
                where("idMember", "==", idMember)
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("[EnrollmentService] Erro ao listar todas as matrículas:", error);
            return [];
        }
    },

    /**
     * Matrícula regular em uma ou mais turmas (Class) a partir de uma data.
     * 
     * Para cada turma:
     * 1. Cria doc de matrícula em enrollments
     * 2. Adiciona o aluno ao array `attendances` de cada sessão futura
     * 3. Incrementa `enrolledCount` em cada sessão futura
     * 4. Incrementa `enrolledCount` na turma (class)
     */
    enrollMember: async (idTenant, user, options) => {
        const { idMember, idMembership, memberName } = options;

        // Suporte ao formato antigo (classIds + startDate) e novo (selectedClasses: [{idClass, startDate}])
        const fallbackStartDate = options.startDate || toISODate(new Date());
        const selectedClasses = options.selectedClasses || (options.classIds || []).map(id => ({ idClass: id, startDate: fallbackStartDate }));

        if (!idTenant || !idMember || selectedClasses.length === 0) {
            throw new Error("Parâmetros incompletos para matrícula");
        }

        try {
            // ── QUERIES FORA DA TRANSAÇÃO (Limitação Web SDK) ──
            const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
            const sessionsRef = collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS);

            // 1. Coletar ref das sessões futuras afetadas
            const sessionRefsToUpdate = [];
            for (const item of selectedClasses) {
                const classStartDateStr = item.startDate || fallbackStartDate;
                const sessionsQuery = query(
                    sessionsRef,
                    where("idClass", "==", item.idClass),
                    where("sessionDate", ">=", classStartDateStr)
                );
                const sessionsSnap = await getDocs(sessionsQuery);
                sessionsSnap.docs.forEach(sessionDoc => sessionRefsToUpdate.push(sessionDoc.ref));
            }

            // 2. Coletar matrículas regulares antigas para o Activity Summary
            const existingQ = query(enrollmentsRef,
                where("idMember", "==", idMember),
                where("status", "==", "active"),
                where("type", "==", "regular")
            );
            const existingEnrollmentsSnap = await getDocs(existingQ);
            const existingActivitiesSet = new Set();
            existingEnrollmentsSnap.docs.forEach(docSnap => {
                 const data = docSnap.data();
                 if (data.idActivity) existingActivitiesSet.add(data.idActivity);
            });

            // ── INÍCIO DA TRANSAÇÃO ATÔMICA ──
            await runTransaction(db, async (t) => {
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
                const summaryRef = doc(db, "tenants", idTenant, COLLECTIONS.METRICS, "activity_members");
                
                // --- LEITURAS ---
                const memberSnap = await t.get(memberRef);
                let memberStatus = memberSnap.exists() ? memberSnap.data().status || 'active' : 'active';
                
                if (!options.skipStatusCheck && (memberStatus === 'inactive' || memberStatus === 'lead')) {
                    throw new Error("Aluno inativo ou lead só pode ser agendado para aula única (Experimental). Utilize a opção correta.");
                }

                // Carregar cache de dados
                const summarySnap = await t.get(summaryRef);
                
                const classesData = {};
                for (const item of selectedClasses) {
                    const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, item.idClass);
                    const classSnap = await t.get(classRef);
                    if (classSnap.exists()) {
                        classesData[item.idClass] = { ref: classRef, data: classSnap.data() };
                    }
                }

                const sessionsData = [];
                for (const ref of sessionRefsToUpdate) {
                    const snap = await t.get(ref);
                    if (snap.exists()) {
                        sessionsData.push({ ref: ref, data: snap.data() });
                    }
                }

                // --- ESCRITAS ---
                const newActivitiesAdded = {}; // Para o summary

                for (const item of selectedClasses) {
                    const idClass = item.idClass;
                    const classObj = classesData[idClass];
                    if (!classObj) continue;

                    const classData = classObj.data;
                    const classRef = classObj.ref;
                    const classStartDateStr = item.startDate || fallbackStartDate;

                    // 1. Criar novo doc de matrícula em Enrollments
                    const newEnrollmentRef = doc(enrollmentsRef);
                    t.set(newEnrollmentRef, {
                        idMember,
                        memberName,
                        idClass,
                        idMembership: idMembership || null,
                        type: "regular",
                        startDate: classStartDateStr,
                        status: "active",
                        idActivity: classData.idActivity || "",
                        activityName: classData.activityName || "",
                        activityColor: classData.activityColor || "#3b82f6",
                        weekday: classData.weekday ?? null,
                        weekdayLabel: WEEKDAY_LABELS[classData.weekday] || "",
                        startTime: classData.startTime || "",
                        endTime: classData.endTime || "",
                        employeeName: classData.employeeName || "",
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        createdBy: {
                            uid: user?.uid || 'system',
                            name: user?.displayName || user?.name || user?.email || 'Sistema'
                        }
                    });

                    // 2. Incrementar contador oficial da Class
                    t.update(classRef, {
                        enrolledCount: increment(1),
                        updatedAt: serverTimestamp()
                    });

                    // 3. Atualizar Attendances (Participantes) nas Sessões 
                    const filteredSessions = sessionsData.filter(
                        s => s.data.idClass === idClass && s.data.sessionDate >= classStartDateStr
                    );
                    
                    for (const session of filteredSessions) {
                        const currentAttendances = session.data.attendances || [];
                        
                        // Proteção estrita contra duplicação de presenças na mesma sessão
                        if (!currentAttendances.some(a => a.idMember === idMember)) {
                            currentAttendances.push({
                                idEnrollment: newEnrollmentRef.id,
                                idMember,
                                memberName,
                                type: "REGULAR",
                                status: "BOOKED"
                            });

                            const regularCount = currentAttendances.filter(a => a.type === "REGULAR").length;
                            const trialCount = currentAttendances.filter(a => a.type === "TRIAL").length;

                            t.update(session.ref, {
                                attendances: currentAttendances,
                                enrolledCount: regularCount,
                                experimentalCount: trialCount,
                                updatedAt: serverTimestamp()
                            });
                        }
                    }

                    // Verifica se essa matrícula gera +1 de estatística global no Summary (usuário novo na atividade)
                    if (classData.idActivity && !existingActivitiesSet.has(classData.idActivity)) {
                        newActivitiesAdded[classData.idActivity] = {
                            actId: classData.idActivity,
                            name: classData.activityName || "",
                            color: classData.activityColor || "#3b82f6"
                        };
                    }
                }

                // 4. Update único atômico no Documento Summary (Metrics)
                let totalNewActivities = 0;
                let summaryUpdates = {};
                
                for (const actInfo of Object.values(newActivitiesAdded)) {
                    summaryUpdates[`activities.${actInfo.actId}.name`] = actInfo.name;
                    summaryUpdates[`activities.${actInfo.actId}.color`] = actInfo.color;
                    summaryUpdates[`activities.${actInfo.actId}.count`] = increment(1);
                    totalNewActivities++;
                    existingActivitiesSet.add(actInfo.actId); // Marca como computado caso compre N turmas da mesma att
                }

                if (totalNewActivities > 0) {
                    if (!summarySnap.exists()) {
                        // Mock object se n tiver docs
                        let newSummaryData = {
                            type: "ACTIVITY_MEMBERS_SUMMARY",
                            activities: {},
                            totalEnrolled: totalNewActivities,
                            updatedAt: serverTimestamp()
                        };
                        for (const actInfo of Object.values(newActivitiesAdded)) {
                           newSummaryData.activities[actInfo.actId] = { name: actInfo.name, color: actInfo.color, count: 1 };
                        }
                        t.set(summaryRef, newSummaryData);
                    } else {
                        summaryUpdates.totalEnrolled = increment(totalNewActivities);
                        summaryUpdates.updatedAt = serverTimestamp();
                        t.update(summaryRef, summaryUpdates);
                    }
                }
            });

            // ── QUERIES PÓS-TRANSAÇÃO (UI / Auditoria) ──
            
            // Atualizar UI array de atividades do membro
            await EnrollmentService._updateMemberActivities(idTenant, idMember);

            // Log de auditoria fire-and-forget
            const auditClassIds = selectedClasses.map(c => c.idClass);
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.ENROLLMENTS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: idMember,
                entityName: memberName,
                description: `Matriculou "${memberName}" em ${auditClassIds.length} turma(s) via Transaction`,
                details: { classes: auditClassIds, type: "regular", method: "transaction" }
            });

            return true;
        } catch (error) {
            console.error("[EnrollmentService] Erro na transação de matrícula:", error);
            throw error;
        }
    },

    /**
     * Agendar Aula Experimental ou Avulsa em uma SESSÃO específica.
     * 
     * 1. Cria doc de matrícula tipo "trial" em enrollments
     * 2. Adiciona o aluno ao array `attendances` da sessão com type="TRIAL"
     * 3. Incrementa `experimentalCount` na sessão
     * 4. NÃO altera o enrolledCount da turma (class) — experimentais não contam como regulares
     */
    scheduleTrialClass: async (idTenant, user, options) => {
        const { idMember, sessionId, memberName } = options;
        if (!idTenant || !idMember || !sessionId) {
            throw new Error("Parâmetros incompletos para aula experimental");
        }

        try {
            const batch = writeBatch(db);

            // 1. Criar o documento de Matrícula tipo "trial"
            const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
            const newEnrollmentRef = doc(enrollmentsRef);

            // Buscar sessão
            const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, sessionId);
            const sessionSnap = await getDoc(sessionRef);

            if (!sessionSnap.exists()) {
                throw new Error("Sessão não existe.");
            }

            const sessionData = sessionSnap.data();

            // Buscar membro para desnormalizar phone/email
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            const memberSnap = await getDoc(memberRef);

            if (!memberSnap.exists()) {
                throw new Error("Membro não encontrado.");
            }

            const memberData = memberSnap.data();

            const enrollmentData = {
                // Dados do membro (desnormalizados)
                idMember,
                memberName,
                memberPhone: memberData.phone || "",
                memberEmail: memberData.email || "",

                // Dados da turma
                idClass: sessionData.idClass || null,

                // Dados da sessão (desnormalizados)
                sessionId,
                sessionDate: sessionData.sessionDate,
                startTime: sessionData.startTime || "",
                endTime: sessionData.endTime || "",
                activityName: sessionData.activityName || "",

                // Dados do professor (desnormalizados)
                idEmployee: sessionData.idEmployee || "",
                employeeName: sessionData.employeeName || "",

                // Dados do consultor (desnormalizados do membro)
                idConsultant: memberData.consultant?.uid || null,
                consultantName: memberData.consultant?.name || null,

                // Tipo e status
                type: "trial",
                status: "active",
                attendanceStatus: TRIAL_ATTENDANCE_STATUS.PENDING, // PENDING → PRESENT | ABSENT (atualizado na chamada)

                // Auditoria
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: {
                    uid: user?.uid || 'system',
                    name: user?.displayName || user?.name || user?.email || 'Sistema'
                }
            };

            batch.set(newEnrollmentRef, enrollmentData);

            // 2. Desnormalizar os dados do aluno dentro do documento da sessão
            const currentAttendances = sessionData.attendances || [];

            if (!currentAttendances.some(a => a.idMember === idMember)) {
                currentAttendances.push({
                    idEnrollment: newEnrollmentRef.id,
                    idMember,
                    memberName,
                    type: "TRIAL",
                    status: "BOOKED"
                });

                // Recalcular contadores separados
                const regularCount = currentAttendances.filter(a => a.type === "REGULAR").length;
                const trialCount = currentAttendances.filter(a => a.type === "TRIAL").length;

                batch.update(sessionRef, {
                    attendances: currentAttendances,
                    enrolledCount: regularCount,
                    experimentalCount: trialCount,
                    updatedAt: serverTimestamp()
                });
            } else {
                throw new Error("O aluno já está registrado nesta sessão.");
            }

            await batch.commit();

            // Auditoria
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.ENROLLMENTS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: idMember,
                entityName: memberName,
                description: `Agendou aula experimental para "${memberName}"`,
                details: { sessionId, type: "trial" }
            });

            return true;
        } catch (error) {
            console.error("[EnrollmentService] Erro ao agendar aula experimental:", error);
            throw error;
        }
    },

    /**
     * Cancelar uma matrícula.
     * 
     * 1. Marca o doc de enrollment como status="canceled"
     * 2. Remove o aluno do array `attendances` de sessões FUTURAS (de hoje em diante)
     * 3. Recalcula enrolledCount e experimentalCount em cada sessão afetada
     * 4. Decrementa enrolledCount na turma (class) se era regular
     * 5. Marca o membro como "inactive"
     * NÃO afeta sessões passadas.
     */
    cancelEnrollment: async (idTenant, user, enrollment) => {
        const { id: enrollmentId, idMember, idClass, type } = enrollment;
        if (!idTenant || !enrollmentId || !idMember) {
            throw new Error("Parâmetros incompletos para cancelar matrícula");
        }

        try {
            const todayStr = toISODate(new Date());
            const batch = writeBatch(db);

            // 1. Marcar a matrícula como cancelada
            const enrollmentRef = doc(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS, enrollmentId);
            batch.update(enrollmentRef, {
                status: "canceled",
                canceledAt: serverTimestamp(),
                canceledBy: user?.uid || 'system',
                updatedAt: serverTimestamp()
            });

            // 2. Se era regular, decrementar enrolledCount na turma
            if (type === 'regular' && idClass) {
                const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, idClass);
                batch.update(classRef, {
                    enrolledCount: increment(-1),
                    updatedAt: serverTimestamp()
                });
            }

            // 3. Remover o aluno das sessões FUTURAS (de hoje em diante)
            if (idClass && type === 'regular') {
                // Regular: afeta todas sessões futuras da turma
                const sessionsRef = collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS);
                const sessionsQuery = query(
                    sessionsRef,
                    where("idClass", "==", idClass),
                    where("sessionDate", ">=", todayStr)
                );
                const sessionsSnap = await getDocs(sessionsQuery);

                sessionsSnap.docs.forEach(sessionDoc => {
                    const sessionData = sessionDoc.data();
                    const currentAttendances = sessionData.attendances || [];

                    // Remover o aluno do array
                    const updatedAttendances = currentAttendances.filter(a => a.idMember !== idMember);

                    // Só atualiza se realmente removeu alguém
                    if (updatedAttendances.length !== currentAttendances.length) {
                        const regularCount = updatedAttendances.filter(a => a.type === "REGULAR").length;
                        const trialCount = updatedAttendances.filter(a => a.type === "TRIAL").length;

                        batch.update(sessionDoc.ref, {
                            attendances: updatedAttendances,
                            enrolledCount: regularCount,
                            experimentalCount: trialCount,
                            updatedAt: serverTimestamp()
                        });
                    }
                });
            } else if (enrollment.sessionId && type === 'trial') {
                // Trial: afeta apenas a sessão específica (somente se for futura)
                const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, enrollment.sessionId);
                const sessionSnap = await getDoc(sessionRef);

                if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.data();
                    const sessionDate = sessionData.sessionDate;

                    // Só remove se a sessão for de hoje em diante
                    if (sessionDate >= todayStr) {
                        const currentAttendances = sessionData.attendances || [];
                        const updatedAttendances = currentAttendances.filter(a => a.idMember !== idMember);

                        if (updatedAttendances.length !== currentAttendances.length) {
                            const regularCount = updatedAttendances.filter(a => a.type === "REGULAR").length;
                            const trialCount = updatedAttendances.filter(a => a.type === "TRIAL").length;

                            batch.update(sessionRef, {
                                attendances: updatedAttendances,
                                enrolledCount: regularCount,
                                experimentalCount: trialCount,
                                updatedAt: serverTimestamp()
                            });
                        }
                    }
                }
            }

            // 4. (Removido: a inativação do aluno não é mais feita ao cancelar matrícula.
            // O aluno só fica inativo se a membership for cancelada/expirada)

            await batch.commit();

            // 5. Decrementar summary de alunos por atividade (somente se membro não tem outra matrícula nesta atividade)
            if (type === 'regular') {
                try {
                    // Buscar idActivity desnormalizado na matrícula
                    let actId = enrollment.idActivity;
                    if (actId) {
                        // Verificar se o membro AINDA tem outra matrícula ativa nesta atividade
                        const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
                        const remainingQ = query(enrollmentsRef,
                            where("idMember", "==", idMember),
                            where("idActivity", "==", actId),
                            where("status", "==", "active"),
                            where("type", "==", "regular")
                        );
                        const remainingSnap = await getDocs(remainingQ);

                        // Se 0 restantes, membro não está mais nesta atividade
                        if (remainingSnap.size === 0) {
                            const summaryRef = doc(db, "tenants", idTenant, COLLECTIONS.METRICS, "activity_members");
                            await updateDoc(summaryRef, {
                                [`activities.${actId}.count`]: increment(-1),
                                totalEnrolled: increment(-1),
                                updatedAt: serverTimestamp()
                            });
                        }
                    }
                } catch (summaryErr) {
                    console.error("[EnrollmentService] Erro ao decrementar summary de atividades:", summaryErr);
                }
            }

            // 6. Atualiza o array de activities do membro para a UI (lista/perfil)
            if (type === 'regular') {
                await EnrollmentService._updateMemberActivities(idTenant, idMember);
            }

            // Auditoria
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.ENROLLMENTS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: enrollmentId,
                entityName: enrollment.memberName || idMember,
                description: `Cancelou matrícula ${type === 'trial' ? 'experimental' : 'regular'} do aluno`,
                details: { idMember, idClass, type }
            });

            return true;
        } catch (error) {
            console.error("[EnrollmentService] Erro ao cancelar matrícula:", error);
            throw error;
        }
    },

    /**
     * Verifica se existem alunos matriculados em uma turma.
     * Consulta a coleção de members que tenham essa turma no array de enrollments.
     *
     * @param {string} idTenant
     * @param {string} idClass
     * @returns {Array} Lista de alunos matriculados (nome e id)
     */
    getEnrolledMembersByClass: async (idTenant, idClass) => {
        if (!idTenant || !idClass) return [];

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                where("enrolledClassIds", "array-contains", idClass)
            );

            const snapshot = await getDocs(q);
            const members = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                members.push({
                    id: docSnap.id,
                    fullName: data.fullName || data.name || "Aluno sem nome",
                    idMember: data.idMember || ""
                });
            });
            return members;
        } catch (error) {
            console.error("[EnrollmentService] Erro ao verificar matrículas:", error);
            return [];
        }
    }
};

export default EnrollmentService;
